import type {
  CustomInterrogationListItem,
  PartInterrogationFile,
} from '@frontend/api';

import type { LatentInterrogationSelection } from '../data/types';

/** Saved rows first, then unsaved defaults. */
const inPreferenceOrder = (
  results: CustomInterrogationListItem[],
): CustomInterrogationListItem[] => [
  ...results.filter((result) => result.id != null),
  ...results.filter((result) => result.id == null),
];

/** First row that has already been run. */
export const pickDefault = (
  results: CustomInterrogationListItem[],
): CustomInterrogationListItem | null =>
  inPreferenceOrder(results).find((result) => result.interrogationFileId) ??
  null;

/**
 * First row that has been run, matching after a refetch. documentHash is not
 * unique: two saved rows can share one, and a finished run regenerates
 * interrogationFileId, so the hash is what the previous selection can be
 * matched on.
 *
 * A row that has not been run is no match: its results are gone, as when a mapping
 * save clears them, and the selection should be dropped rather than left
 * showing an empty feature list.
 */
export const findByDocumentHash = (
  results: CustomInterrogationListItem[],
  documentHash: string,
): CustomInterrogationListItem | null =>
  inPreferenceOrder(results).find(
    (result) =>
      result.documentHash === documentHash &&
      Boolean(result.interrogationFileId),
  ) ?? null;

export const findByInterrogationFileId = (
  results: CustomInterrogationListItem[],
  interrogationFileId: number,
): CustomInterrogationListItem | null =>
  inPreferenceOrder(results).find(
    (result) => result.interrogationFileId === interrogationFileId,
  ) ?? null;

/** Annotation part-interrogation file with no row in the custom list. */
export const toLatentSelection = ({
  id,
  documentHash,
  task,
}: PartInterrogationFile): LatentInterrogationSelection => ({
  task,
  documentHash,
  interrogationFileId: id,
  name: task,
});
